<template>
  <div>
    <!-- Header -->

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-4">
      <b-spinner variant="primary" label="Cargando..."></b-spinner>
      <p class="mt-2">Cargando datos...</p>
    </div>

    <b-card class="mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h3 class="mb-1">Gestión de Asignaciones Académicas</h3>
          <p class="text-muted mb-0">Asignar cursos, horarios y maestros a las secciones</p>
        </div>

        <!-- Botones de Exportación -->
        <b-button
          variant="outline-success"
          @click="showExportOptions = !showExportOptions"
          v-b-tooltip.hover
          title="Exportar Reportes"
          class="mr-2"
        >
          <feather-icon icon="DownloadIcon" class="mr-1" />
          Exportar
        </b-button>

        <b-button
          variant="primary"
          @click="showAssignmentForm = true"
          v-b-tooltip.hover
          title="Nueva Asignación"
        >
          <feather-icon icon="PlusIcon" class="mr-1" />
          Nueva Asignación
        </b-button>
      </div>

      <!-- Menú de Exportación -->
      <b-collapse v-model="showExportOptions" class="mt-3">
        <b-card>
          <h6 class="mb-3">Opciones de Exportación</h6>
          <div class="d-flex flex-wrap gap-2">
            <b-button
              variant="outline-danger"
              size="sm"
              @click="exportToPDF"
              :disabled="assignments.length === 0"
            >
              <feather-icon icon="FileTextIcon" class="mr-1" />
              Exportar PDF
            </b-button>

            <b-button
              variant="outline-success"
              size="sm"
              @click="exportToExcel"
              :disabled="assignments.length === 0"
            >
              <feather-icon icon="FileIcon" class="mr-1" />
              Exportar Excel
            </b-button>
          </div>
        </b-card>
      </b-collapse>

    </b-card>

    <!-- Alerta de Error del Servidor -->
    <b-alert
      v-if="serverError"
      variant="danger"
      show
      dismissible
      @dismissed="serverError = null"
      class="mb-4"
    >
      <div class="d-flex align-items-start">
        <feather-icon icon="AlertTriangleIcon" size="20" class="mr-2 mt-1" />
        <div>
          <h6 class="alert-heading mb-1">Error al procesar la solicitud</h6>
          <p class="mb-0">{{ serverError }}</p>
        </div>
      </div>
    </b-alert>

    <!-- Formulario de Nueva Asignación -->
    <b-card v-if="showAssignmentForm" class="mb-4">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0">
          <feather-icon icon="EditIcon" class="mr-1" />
          {{ isEditing ? 'Editar Asignación' : 'Nueva Asignación' }}
        </h5>
        <b-button variant="outline-secondary" @click="closeForm">
          <feather-icon icon="XIcon" />
        </b-button>
      </div>

      <b-form @submit.prevent="handleSubmit">
        <b-row>
          <!-- Programación Académica -->
          <b-col md="6">
            <b-form-group label="Programación Académica *" label-for="programacion">
              <v-select
                id="programacion"
                v-model="form.programacionAcademicaId"
                :options="schedulingsWithDetails"
                label="text"
                :reduce="o => o.value"
                placeholder="Seleccionar programación"
                @input="onProgramacionChange"
              />
              <b-form-invalid-feedback v-if="validation.programacionAcademicaId === false">
                Debe seleccionar una programación académica
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <!-- Curso -->
          <b-col md="6">
            <b-form-group label="Curso *" label-for="curso">
              <v-select
                id="curso"
                v-model="form.cursoId"
                :options="cursosOptions"
                label="text"
                :reduce="o => o.value"
                placeholder="Seleccionar curso"
                @input="onCursoChange"
              />
              <b-form-invalid-feedback v-if="validation.cursoId === false">
                Debe seleccionar un curso
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <!-- Sección -->
          <b-col md="4">
            <b-form-group label="Sección *" label-for="seccion">
              <b-form-input
                id="seccion"
                v-model="form.codigoSeccion"
                placeholder="Ej: A, B, C"
                :state="validation.codigoSeccion"
                required
              />
              <b-form-invalid-feedback>
                Debe ingresar un código de sección
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <!-- Edificio -->
          <b-col md="4">
            <b-form-group label="Edificio *" label-for="edificio">
              <v-select
                id="edificio"
                v-model="form.edificioId"
                :options="edificiosOptions"
                label="text"
                :reduce="o => o.value"
                placeholder="Seleccionar edificio"
                @input="onEdificioChange"
              />
              <b-form-invalid-feedback v-if="validation.edificioId === false">
                Debe seleccionar un edificio
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <!-- Salón -->
          <b-col md="4">
            <b-form-group label="Salón *" label-for="salon">
              <v-select
                id="salon"
                v-model="form.salonId"
                :options="salonesOptions"
                label="text"
                :reduce="o => o.value"
                placeholder="Seleccionar salón"
              />
              <b-form-invalid-feedback v-if="validation.salonId === false">
                Debe seleccionar un salón
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <!-- Día de la Semana -->
          <b-col md="3">
            <b-form-group label="Día *" label-for="dia">
              <v-select
                id="dia"
                v-model="form.diaSemana"
                :options="diasOptions"
                label="text"
                :reduce="o => o.value"
                placeholder="Seleccionar día"
              />
              <b-form-invalid-feedback v-if="validation.diaSemana === false">
                Debe seleccionar un día
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <!-- Hora Inicio -->
          <b-col md="3">
            <b-form-group label="Hora Inicio *" label-for="horaInicio">
              <b-form-input
                id="horaInicio"
                v-model="form.horaInicio"
                type="time"
                :state="validation.horaInicio"
                placeholder="HH:MM"
                required
              />
              <b-form-invalid-feedback>
                Debe seleccionar hora de inicio
              </b-form-invalid-feedback>
              <small class="form-text text-muted">
                Formato: HH:MM (ej: 08:00, 14:30)
              </small>
            </b-form-group>
          </b-col>

          <!-- Hora Fin -->
          <b-col md="3">
            <b-form-group label="Hora Fin *" label-for="horaFin">
              <b-form-input
                id="horaFin"
                v-model="form.horaFin"
                type="time"
                :state="validation.horaFin"
                placeholder="HH:MM"
                required
              />
              <b-form-invalid-feedback>
                Debe seleccionar hora de fin
              </b-form-invalid-feedback>
              <small class="form-text text-muted">
                Formato: HH:MM (ej: 10:00, 16:30)
              </small>
            </b-form-group>
          </b-col>

          <!-- Catedrático -->
          <b-col md="3">
            <b-form-group label="Catedrático *" label-for="catedratico">
              <v-select
                id="catedratico"
                v-model="form.catedraticoId"
                :options="catedraticosOptions"
                label="text"
                :reduce="o => o.value"
                placeholder="Seleccionar catedrático"
                @input="onCatedraticoChange"
              />
              <b-form-invalid-feedback v-if="validation.catedraticoId === false">
                Debe seleccionar un catedrático
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Observaciones -->
        <b-row>
          <b-col md="12">
            <b-form-group label="Observaciones" label-for="observaciones">
              <b-form-textarea
                id="observaciones"
                v-model="form.observaciones"
                placeholder="Observaciones adicionales (opcional)"
                rows="3"
                maxlength="500"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Alertas de Validación -->
        <b-alert v-if="conflicts.length > 0" variant="danger" show class="mt-3">
          <h6 class="alert-heading mb-2">
            <feather-icon icon="AlertTriangleIcon" class="mr-1" />
            Conflictos Detectados
          </h6>
          <ul class="mb-0">
            <li v-for="conflict in conflicts" :key="conflict.id">
              {{ conflict.message }}
            </li>
          </ul>
        </b-alert>

        <!-- Botones de Acción -->
        <div class="d-flex justify-content-end mt-4">
          <b-button variant="outline-secondary" @click="closeForm" class="mr-2">
            Cancelar
          </b-button>
          <b-button 
            type="submit" 
            variant="primary" 
            :disabled="conflicts.length > 0"
          >
            <feather-icon icon="SaveIcon" class="mr-1" />
            {{ isEditing ? 'Actualizar' : 'Guardar' }} Asignación
          </b-button>
        </div>
      </b-form>
    </b-card>

    <b-card class="mb-4">
      <h6 class="mb-3">Filtros de Búsqueda</h6>
      <b-row>
        <b-col md="3">
          <b-form-group label="Programación">
            <b-form-input
              v-model="filter.programacion"
              placeholder="Buscar por carrera..."
              debounce="300"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Curso">
            <b-form-input
              v-model="filter.curso"
              placeholder="Buscar curso..."
              debounce="300"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Catedrático">
            <b-form-input
              v-model="filter.catedratico"
              placeholder="Buscar catedrático..."
              debounce="300"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Estado">
            <b-form-select v-model="filter.estado" :options="[
              { value: '', text: 'Todo' },
              { value: 'true', text: 'Activo' },
              { value: 'false', text: 'Inactivo' }
            ]" />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <!-- Lista de Asignaciones Existentes -->
    <b-card>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0">
          <feather-icon icon="ListIcon" class="mr-1" />
          Asignaciones Existentes
        </h5>
        <b-badge variant="info" class="p-2">
          {{ assignments.length }} asignaciones
        </b-badge>
      </div>

      <div v-if="assignments.length === 0" class="text-center py-4 text-muted">
        <feather-icon icon="CalendarIcon" size="48" class="mb-3" />
        <h6>No hay asignaciones registradas</h6>
        <p class="mb-0">Crea tu primera asignación usando el botón "Nueva Asignación"</p>
      </div>

      <b-table
        v-else
        :items="filteredAssignments"
        :fields="tableFields"
        :per-page="pagination.perPage"
        :current-page="pagination.currentPage"
        responsive
        striped
        hover
        class="mb-0"
      >
        <template #cell(programacion)="row">
          <div>
            <strong>{{ row.item.periodoCodigo }}</strong>
            <br>
            <small class="text-muted">{{ row.item.carreraNombre }}</small>
          </div>
        </template>

        <template #cell(curso)="row">
          <div>
            <strong>{{ row.item.cursoNombre }}</strong>
            <br>
            <small class="text-muted">Sección: {{ row.item.seccion }}</small>
          </div>
        </template>

        <template #cell(ubicacion)="row">
          <div>
            <strong>{{ row.item.edificioNombre }}</strong>
            <br>
            <small class="text-muted">Salón: {{ row.item.salonNumero }}</small>
          </div>
        </template>

        <template #cell(horario)="row">
          <div>
            <strong>{{ row.item.diaSemana }}</strong>
            <br>
            <small class="text-muted">{{ row.item.horaInicio }} - {{ row.item.horaFin }}</small>
          </div>
        </template>

        <template #cell(catedratico)="row">
          <div>
            <strong>{{ row.item.catedraticoNombre }}</strong>
            <br>
            <small class="text-muted">{{ row.item.catedraticoEspecialidad || 'Sin especialidad' }}</small>
          </div>
        </template>

        <template #cell(estado)="row">
          <b-badge :variant="row.item.estado ? 'success' : 'secondary'">
            {{ row.item.estado ? 'Activo' : 'Inactivo' }}
          </b-badge>
        </template>

        <template #cell(acciones)="row">
          <div class="d-flex">
            <b-button
              variant="outline-primary"
              size="sm"
              @click="editAssignment(row.item)"
              v-b-tooltip.hover
              title="Editar"
              class="mr-1"
            >
              <feather-icon icon="EditIcon" size="14" />
            </b-button>
            <b-button
              variant="outline-danger"
              size="sm"
              @click="deleteAssignment(row.item)"
              v-b-tooltip.hover
              title="Eliminar"
            >
              <feather-icon icon="TrashIcon" size="14" />
            </b-button>
          </div>
        </template>
      </b-table>

      <!-- Paginación -->
      <b-pagination
        v-model="pagination.currentPage"
        :total-rows="pagination.totalRows"
        :per-page="pagination.perPage"
        align="center"
        class="mt-3"
      />
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BFormSelect, BFormInput, BFormTextarea,
  BButton, BSpinner, BTable, BBadge, BAlert,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import programacionAcademicaService from '@/services/programacionAcademicaService'
import asignacionesService from '@/services/asignacionesService'
import catedraticosService from '@/services/catedraticosService'
import salonesService from '@/services/salonesService'
import edificiosService from '@/services/edificiosService'
import cursosService from '@/services/cursosService'
import vSelect from 'vue-select'

//import ExportService from '@/services/exportService'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BFormInput,
    BFormTextarea,
    BButton,
    BSpinner,
    BTable,
    BBadge,
    BAlert,
    vSelect,
  },
  data() {
    return {
      // Datos de la API
      schedulings: [],
      cursos: [],
      catedraticos: [],
      salones: [],
      edificios: [],
      assignments: [],
      
      // Estado del formulario
      showAssignmentForm: false,
      isEditing: false,
      loading: false,
      
      // Alerta de error del servidor
      serverError: null,
      
      // Formulario
      form: {
        asignacionId: null,
        programacionAcademicaId: null,
        cursoId: null,
        codigoSeccion: '',
        edificioId: null,
        salonId: null,
        diaSemana: null,
        horaInicio: null,
        horaFin: null,
        catedraticoId: null,
        observaciones: '',
      },
      
      // Validaciones
      validation: {
        programacionAcademicaId: null,
        cursoId: null,
        codigoSeccion: null,
        edificioId: null,
        salonId: null,
        diaSemana: null,
        horaInicio: null,
        horaFin: null,
        catedraticoId: null,
      },
      
      // Conflictos
      conflicts: [],
      
      // Opciones para selects
      diasOptions: [
        { value: null, text: 'Seleccionar día' },
        { value: 'Lunes', text: 'Lunes' },
        { value: 'Martes', text: 'Martes' },
        { value: 'Miércoles', text: 'Miércoles' },
        { value: 'Jueves', text: 'Jueves' },
        { value: 'Viernes', text: 'Viernes' },
        { value: 'Sábado', text: 'Sábado' },
        { value: 'Domingo', text: 'Domingo' },
      ],
      
      // Campos de la tabla
      tableFields: [
        { key: 'programacion', label: 'Programación', sortable: true },
        { key: 'curso', label: 'Curso', sortable: true },
        { key: 'ubicacion', label: 'Ubicación', sortable: true },
        { key: 'horario', label: 'Horario', sortable: true },
        { key: 'catedratico', label: 'Catedrático', sortable: true },
        { key: 'estado', label: 'Estado', sortable: true },
        { key: 'acciones', label: 'Acciones', sortable: false },
      ],

      //Manejo de cache
      dataCache: {
        schedulings: null,
        cursos: null,
        catedraticos: null,
        salones: null,
        edificios: null,
        assignments: null,
      },

      cacheTimestamp: null,
      CACHE_DURATION: 5 * 60 * 1000, // 5 minutos

      filter: {
        programacion: '',
        curso: '',
        catedratico: '',
        estado: ''
      },
      pagination: {
        currentPage: 1,
        perPage: 10,
        totalRows: 0
      },

      // Exportación
      showExportOptions: false,
      showCatedraticoExport: false,
      showSalonExport: false,
      selectedCatedratico: null,
      selectedSalon: null,

    }
  },
  async mounted() {
    await this.loadData()
  },
  computed: {
    schedulingsWithDetails() {
      return [
        { value: null, text: 'Seleccionar programación' },
        ...this.schedulings.map(s => ({
          value: s.programacionAcademicaId,
          text: `${s.periodoCodigo} - ${s.carreraNombre}`,
          ...s
        }))
      ]
    },
    cursosOptions() {
      return [
        { value: null, text: 'Seleccionar curso' },
        ...this.cursos.map(c => ({
          value: c.cursoId,
          text: c.nombre,
          ...c
        }))
      ]
    },
    catedraticosOptions() {
      return [
        { value: null, text: 'Seleccionar catedrático' },
        ...this.catedraticos.map(c => ({
          value: c.catedraticoId,
          text: c.nombreCompleto,
          ...c
        }))
      ]
    },
    edificiosOptions() {
      return [
        { value: null, text: 'Seleccionar edificio' },
        ...this.edificios.map(e => ({
          value: e.edificioId,
          text: e.nombre,
          ...e
        }))
      ]
    },
    salonesOptions() {
      // CORRECCIÓN: Filtrar salones por edificio seleccionado
      if (!this.form.edificioId) {
        return [{ value: null, text: 'Primero seleccione un edificio' }]
      }
      
      const salonesFiltrados = this.salones.filter(s => s.edificioId === this.form.edificioId)
      
      if (salonesFiltrados.length === 0) {
        return [{ value: null, text: 'No hay salones para este edificio' }]
      }
      
      return [
        { value: null, text: 'Seleccionar salón' },
        ...salonesFiltrados.map(s => ({
          value: s.salonId,
          text: `${s.numero} (Cap: ${s.capacidad})`,
          ...s
        }))
      ]
    },

    // Opciones para exportación de catedráticos
    catedraticosExportOptions() {
      const catedraticosUnicos = [...new Set(this.assignments.map(a => a.catedraticoNombre))]
      return [
        { value: null, text: 'Seleccionar catedrático' },
        ...catedraticosUnicos.map(name => ({ value: name, text: name }))
      ]
    },

    // Opciones para exportación de salones
    salonesExportOptions() {
      const salonesUnicos = [...new Set(this.assignments.map(a => ({
        value: { numero: a.salonNumero, edificio: a.edificioNombre },
        text: `${a.salonNumero} - ${a.edificioNombre}`
      })))]
      
      return [
        { value: null, text: 'Seleccionar salón' },
        ...salonesUnicos
      ]
    },

    filteredAssignments() {
      let filtered = this.assignments
      
      if (this.filter.programacion) {
        filtered = filtered.filter(a => 
          a.carreraNombre.toLowerCase().includes(this.filter.programacion.toLowerCase())
        )
      }
      
      if (this.filter.curso) {
        filtered = filtered.filter(a => 
          a.cursoNombre.toLowerCase().includes(this.filter.curso.toLowerCase())
        )
      }
      
      if (this.filter.catedratico) {
        filtered = filtered.filter(a => 
          a.catedraticoNombre.toLowerCase().includes(this.filter.catedratico.toLowerCase())
        )
      }
      
      if (this.filter.estado !== '') {
        filtered = filtered.filter(a => a.estado === (this.filter.estado === 'true'))
      }
      
      this.pagination.totalRows = filtered.length
      return filtered
    },
    
  },
  watch: {
    'form.edificioId'() {
      // Limpiar salón cuando cambia el edificio
      this.form.salonId = null
      this.checkConflicts()
    },
    'form.diaSemana'() {
      this.checkConflicts()
    },
    'form.horaInicio'() {
      this.checkConflicts()
    },
    'form.horaFin'() {
      this.checkConflicts()
    },
    'form.catedraticoId'() {
      this.checkConflicts()
    },
    'form.salonId'() {
      this.checkConflicts()
    },
    'form.codigoSeccion'() {
      this.checkConflicts()
    },
    'form.programacionAcademicaId'() {
      this.checkConflicts()
    },
  },
  methods: {
    async loadData() {
      this.loading = true

      // Verificar cache
      if (this.isCacheValid()) {
        this.loadFromCache()
        this.loading = false
        return
      }

      try {
        console.log('📋 Cargando datos para asignaciones...')
        
        const [
          schedulingsResponse, 
          cursosResponse, 
          catedraticosResponse, 
          salonesResponse, 
          edificiosResponse,
          assignmentsResponse
        ] = await Promise.all([
          programacionAcademicaService.getAll(),
          cursosService.getAll(),
          catedraticosService.getActive(),
          salonesService.getActive(),
          edificiosService.getAll(),
          asignacionesService.getAll()
        ])

        this.schedulings = schedulingsResponse.data || []
        this.cursos = cursosResponse || []
        this.catedraticos = catedraticosResponse || []
        this.salones = salonesResponse || []
        this.edificios = edificiosResponse || []
        
        // Las asignaciones ya vienen con datos de display desde la API
        this.assignments = assignmentsResponse || []
        console.log('✅ Asignaciones cargadas desde API:', this.assignments)

        console.log('✅ Datos cargados:', {
          schedulings: this.schedulings.length,
          cursos: this.cursos.length,
          catedraticos: this.catedraticos.length,
          salones: this.salones.length,
          edificios: this.edificios.length,
          assignments: this.assignments.length,
        })

        this.saveToCache()
      } catch (error) {
        console.error('Error loading data:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: 'Error al cargar los datos. Intente nuevamente.',
          },
        })
      } finally {
        this.loading = false
      }
    },

    isCacheValid() {
      return this.cacheTimestamp && 
      (Date.now() - this.cacheTimestamp) < this.CACHE_DURATION
    },
  
    saveToCache() {
      this.dataCache = {
        schedulings: this.schedulings,
        cursos: this.cursos,
        catedraticos: this.catedraticos,
        salones: this.salones,
        edificios: this.edificios,
        assignments: this.assignments
      }
      this.cacheTimestamp = Date.now()
    },
    
    loadFromCache() {
      this.schedulings = this.dataCache.schedulings
      this.cursos = this.dataCache.cursos
      this.catedraticos = this.dataCache.catedraticos
      this.salones = this.dataCache.salones
      this.edificios = this.dataCache.edificios
      this.assignments = this.dataCache.assignments
    },
    
    onProgramacionChange() {
      this.validateField('programacionAcademicaId')
    },
    
    onCursoChange() {
      this.validateField('cursoId')
    },
    
    onEdificioChange() {
      this.validateField('edificioId')
      this.form.salonId = null // Limpiar salón
    },
    
    onCatedraticoChange() {
      this.validateField('catedraticoId')
      this.checkConflicts()
    },
    
    validateField(fieldName) {
      const value = this.form[fieldName]
      this.validation[fieldName] = value ? null : false
    },
    
    async checkConflicts() {
      if (!this.form.diaSemana || !this.form.horaInicio || !this.form.horaFin || 
          !this.form.catedraticoId || !this.form.salonId) {
        this.conflicts = []
        return
      }

      this.conflicts = []

      // Verificar conflictos de catedrático (dentro del mismo período académico)
      const catedraticoConflicts = this.assignments.filter(assignment => 
        assignment.programacionAcademicaId === this.form.programacionAcademicaId &&
        assignment.catedraticoId === this.form.catedraticoId &&
        assignment.diaSemana === this.form.diaSemana &&
        this.timeOverlaps(assignment.horaInicio, assignment.horaFin, this.form.horaInicio, this.form.horaFin) &&
        assignment.asignacionId !== this.form.asignacionId
      )

      if (catedraticoConflicts.length > 0) {
        const conflicto = catedraticoConflicts[0]
        this.conflicts.push({
          id: 'catedratico',
          message: `El catedrático "${conflicto.catedraticoNombre}" ya tiene asignado el curso "${conflicto.cursoNombre}" (Sección ${conflicto.seccion}) en el salón ${conflicto.salonNumero} - ${conflicto.edificioNombre}, el día ${this.form.diaSemana} de ${conflicto.horaInicio} a ${conflicto.horaFin} para este mismo período académico.`
        })
      }

      // Verificar conflictos de salón (dentro del mismo período académico)
      const salonConflicts = this.assignments.filter(assignment => 
        assignment.programacionAcademicaId === this.form.programacionAcademicaId &&
        assignment.salonId === this.form.salonId &&
        assignment.diaSemana === this.form.diaSemana &&
        this.timeOverlaps(assignment.horaInicio, assignment.horaFin, this.form.horaInicio, this.form.horaFin) &&
        assignment.asignacionId !== this.form.asignacionId
      )

      if (salonConflicts.length > 0) {
        const conflicto = salonConflicts[0]
        this.conflicts.push({
          id: 'salon',
          message: `El salón ${conflicto.salonNumero} (${conflicto.edificioNombre}) ya está ocupado por el curso "${conflicto.cursoNombre}" (Sección ${conflicto.seccion}), impartido por "${conflicto.catedraticoNombre}", el día ${this.form.diaSemana} de ${conflicto.horaInicio} a ${conflicto.horaFin} para este mismo período académico.`
        })
      }

      // Verificar conflictos de sección (una sección no puede tener dos cursos al mismo tiempo)
      const seccionConflicts = this.assignments.filter(assignment => 
        assignment.programacionAcademicaId === this.form.programacionAcademicaId &&
        assignment.codigoSeccion === this.form.codigoSeccion &&
        assignment.diaSemana === this.form.diaSemana &&
        this.timeOverlaps(assignment.horaInicio, assignment.horaFin, this.form.horaInicio, this.form.horaFin) &&
        assignment.cursoId !== this.form.cursoId && // Diferente curso
        assignment.asignacionId !== this.form.asignacionId
      )

      if (seccionConflicts.length > 0) {
        const conflicto = seccionConflicts[0]
        this.conflicts.push({
          id: 'seccion',
          message: `La sección ${this.form.codigoSeccion} ya tiene asignado el curso "${conflicto.cursoNombre}" en el día ${this.form.diaSemana} de ${conflicto.horaInicio} a ${conflicto.horaFin}. Los estudiantes no pueden tener dos cursos al mismo tiempo.`
        })
      }
    },
    
    timeOverlaps(start1, end1, start2, end2) {
      const time1Start = this.timeToMinutes(start1)
      const time1End = this.timeToMinutes(end1)
      const time2Start = this.timeToMinutes(start2)
      const time2End = this.timeToMinutes(end2)
      
      return time1Start < time2End && time1End > time2Start
    },
    
    timeToMinutes(timeString) {
      const [hours, minutes] = timeString.split(':').map(Number)
      return hours * 60 + minutes
    },
    
    async handleSubmit() {
      // Limpiar error previo
      this.serverError = null
      
      // Validar formulario
      const isValid = this.validateForm()
      if (!isValid) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error de Validación',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: 'Por favor complete todos los campos requeridos.',
          },
        })
        return
      }

      if (this.conflicts.length > 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Conflictos Detectados',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
            text: 'Existen conflictos que deben resolverse antes de guardar.',
          },
        })
        return
      }

      try {
        if (this.isEditing) {
          // Preparar datos para actualización
          const updateData = {
            asignacionId: this.form.asignacionId,
            programacionAcademicaId: this.form.programacionAcademicaId,
            cursoId: this.form.cursoId,
            catedraticoId: this.form.catedraticoId,
            salonId: this.form.salonId,
            horarioId: this.form.horarioId || null,
            diaSemana: this.form.diaSemana,
            horaInicio: this.form.horaInicio,
            horaFin: this.form.horaFin,
            seccion: this.form.codigoSeccion,
            observaciones: this.form.observaciones || '',
            estado: 'Activo',
            estadoActivo: true
          }
          
          await asignacionesService.update(updateData)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Actualización Exitosa',
              icon: 'CheckIcon',
              variant: 'success',
              text: 'La asignación se ha actualizado correctamente.',
            },
          })
        } else {
          // Preparar datos para creación
          const createData = {
            programacionAcademicaId: this.form.programacionAcademicaId,
            cursoId: this.form.cursoId,
            catedraticoId: this.form.catedraticoId,
            salonId: this.form.salonId,
            horarioId: this.form.horarioId || null,
            diaSemana: this.form.diaSemana,
            horaInicio: this.form.horaInicio,
            horaFin: this.form.horaFin,
            seccion: this.form.codigoSeccion,
            observaciones: this.form.observaciones || '',
            estado: 'Activo'
          }
          
          const response = await asignacionesService.create(createData)
          
          // La API ya devuelve la asignación completa con datos de display
          this.assignments.push(response)
          console.log('✅ Asignación agregada a la tabla:', response)
          
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Asignación Creada',
              icon: 'CheckIcon',
              variant: 'success',
              text: 'La asignación se ha creado correctamente.',
            },
          })
        }

        this.closeForm()
        await this.loadData() // Recargar datos
      } catch (error) {
        console.error('Error saving assignment:', error)
        
        // Capturar mensaje de error del servidor
        let errorMessage = 'Error al guardar la asignación. Intente nuevamente.'
        
        if (error.response && error.response.data) {
          // Si el backend devuelve un mensaje de error específico
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message
          } else if (error.response.data.title) {
            errorMessage = error.response.data.title
          }
        }
        
        // Mostrar el error en la alerta visual
        this.serverError = errorMessage
        
        // Scroll hacia arriba para que el usuario vea la alerta
        window.scrollTo({ top: 0, behavior: 'smooth' })
        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: errorMessage,
          },
        })
      }
    },
    
    validateForm() {
      const fields = [
        'programacionAcademicaId',
        'cursoId',
        'codigoSeccion',
        'edificioId',
        'salonId',
        'diaSemana',
        'horaInicio',
        'horaFin',
        'catedraticoId'
      ]

      let isValid = true
      fields.forEach(field => {
        const value = this.form[field]
        this.validation[field] = value ? null : false
        if (!value) isValid = false
      })

      return isValid
    },
    
    closeForm() {
      this.showAssignmentForm = false
      this.isEditing = false
      this.serverError = null // Limpiar error del servidor
      this.resetForm()
    },
    
    resetForm() {
      this.form = {
        asignacionId: null,
        programacionAcademicaId: null,
        cursoId: null,
        codigoSeccion: '',
        edificioId: null,
        salonId: null,
        diaSemana: null,
        horaInicio: null,
        horaFin: null,
        catedraticoId: null,
        observaciones: '',
      }
      
      this.validation = {
        programacionAcademicaId: null,
        cursoId: null,
        codigoSeccion: null,
        edificioId: null,
        salonId: null,
        diaSemana: null,
        horaInicio: null,
        horaFin: null,
        catedraticoId: null,
      }
      
      this.conflicts = []
    },
    
    editAssignment(assignment) {
      this.isEditing = true
      this.showAssignmentForm = true

      // Primero cargar el edificio
      this.form.edificioId = assignment.edificioId

      // Usar $nextTick para esperar a que Vue actualice las opciones
      this.$nextTick(() => {
        this.form.salonId = assignment.salonId
      })

      // Otros campos
      this.form = {
        ...this.form,
        asignacionId: assignment.asignacionId,
        programacionAcademicaId: assignment.programacionAcademicaId,
        cursoId: assignment.cursoId,
        codigoSeccion: assignment.seccion || assignment.codigoSeccion,
        diaSemana: assignment.diaSemana,
        horaInicio: assignment.horaInicio,
        horaFin: assignment.horaFin,
        catedraticoId: assignment.catedraticoId,
        observaciones: assignment.observaciones || '',
      }

      console.log('📝 Editando asignación - Datos cargados:', this.form)
    },
    
    async deleteAssignment(assignment) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `¿Está seguro de que desea eliminar la asignación de ${assignment.cursoNombre} - Sección ${assignment.codigoSeccion}?`,
        {
          title: 'Confirmar Eliminación',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Eliminar',
          cancelTitle: 'Cancelar',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      )

      if (confirmed) {
        try {
          await asignacionesService.delete(assignment.asignacionId)
          this.assignments = this.assignments.filter(a => a.asignacionId !== assignment.asignacionId)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Eliminación Exitosa',
              icon: 'CheckIcon',
              variant: 'success',
              text: 'La asignación se ha eliminado correctamente.',
            },
          })
        } catch (error) {
          console.error('Error deleting assignment:', error)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: 'Error al eliminar la asignación. Intente nuevamente.',
            },
          })
        }
      }
    },

    //Validación de horarios lógicos
    validateTimeRange() {
      if (this.form.horaInicio && this.form.horaFin) {
        const start = this.timeToMinutes(this.form.horaInicio)
        const end = this.timeToMinutes(this.form.horaFin)
        
        if (end <= start) {
          this.conflicts.push({
            id: 'time-range',
            message: 'La hora de fin debe ser posterior a la hora de inicio'
          })
        }
        
        // Validar duración mínima (ej: 30 minutos)
        const duration = end - start
        if (duration < 30) {
          this.conflicts.push({
            id: 'min-duration',
            message: 'La duración mínima de una clase es de 30 minutos'
          })
        }
      }
    },

    // Métodos de Exportación
    async exportToPDF() {
      try {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Generando PDF',
            icon: 'FileTextIcon',
            variant: 'info',
            text: 'Preparando reporte en PDF...',
          },
        })

        await ExportService.exportToPDF(this.assignments)
        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'PDF Exportado',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'El reporte PDF se ha descargado correctamente.',
          },
        })
      } catch (error) {
        console.error('Error exportando PDF:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: 'Error al exportar el PDF. Intente nuevamente.',
          },
        })
      }
    },

    async exportToExcel() {
      try {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Generando Excel',
            icon: 'FileIcon',
            variant: 'info',
            text: 'Preparando reporte en Excel...',
          },
        })

        await ExportService.exportToExcel(this.assignments)
        
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Excel Exportado',
            icon: 'CheckIcon',
            variant: 'success',
            text: 'El reporte Excel se ha descargado correctamente.',
          },
        })
      } catch (error) {
        console.error('Error exportando Excel:', error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: 'Error al exportar el Excel. Intente nuevamente.',
          },
        })
      }
    },

  },
}
</script>

<style scoped>

/* Estilos para los botones de exportación */
.gap-2 > * {
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
}

/* Estilos específicos para el formulario de asignaciones */
.form-group {
  margin-bottom: 1rem;
}

.alert {
  border-radius: 0.375rem;
  animation: slideDown 0.3s ease-out;
}

.alert.alert-danger {
  border-left: 4px solid #ea5455;
  box-shadow: 0 2px 8px rgba(234, 84, 85, 0.15);
}

.alert-heading {
  font-weight: 600;
  color: #721c24;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.table th {
  border-top: none;
  font-weight: 600;
  color: #5a6a85;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.badge {
  font-size: 0.75rem;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
.min-h-200 {
  min-height: 200px;
}
.ghost-card {
  opacity: 0.5;
  background: #F7FAFC;
  border: 1px solid #4299e1;
}
</style>